import { Autocomplete, TextField, Tooltip } from '@mui/material'

import { DigitalTwinPropertyObject, useDigitalTwin } from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel } from 'api/digitalTwin/digitalTwin.types'
import { BASE_SETTING_PRIO } from 'api/digitalTwin/digitalTwinConstants'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import {
  Choices,
  getAutocompleteValueForModelEntityName,
  getAutocompleteValueForProperty,
} from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'

import { getHelpTextPrettyName, getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import { EventChangeRequest, EventPost } from 'views/EventsView/components/events.helper'

type EventPropertiesDropdownsProps = {
  eventPost: EventPost | EventChangeRequest
  linkedObject: { level: DeviationSettingLevel | string; name: string; attribute: string | null }
  index: number
  error?: boolean
  disabled?: boolean
  setEventPost: (event: EventPost | EventChangeRequest) => void
}

export default function EventPropertiesDropdownsSettingInstructions({
  eventPost,
  error,
  linkedObject,
  index,
  disabled,
  setEventPost,
}: EventPropertiesDropdownsProps): ReactElement {
  const { t } = useTranslation()

  const { activeSystem } = useAuth()
  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin, isLoading: isLoadingDigitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const linkedObjectFromInstructions = eventPost?.setting_instructions?.linked_objects

  const noSettingWritePermission = linkedObjectFromInstructions?.[index]?.write === false

  function isWritableObject(p: DigitalTwinPropertyObject | undefined): boolean {
    // If any property for this object has write set to false, user should not be able to create setting on it
    if (p?.configurable_properties === undefined || p?.configurable_properties.length === 0) {
      return p?.write !== false
    } else {
      return p?.configurable_properties?.some((c) => c.write !== false)
    }
  }

  function setPropertyObject(
    list: DigitalTwinPropertyObject[],
    level: DeviationSettingLevel,
    properties?: DigitalTwinPropertyObject[]
  ): void {
    properties?.forEach((p) => {
      p.level = level
      if (isWritableObject(p)) {
        list.push(p)
      }
    })
  }

  const propertyItems = useMemo(() => {
    // Sets objectCategories
    if (!digitalTwin?.model || isLoadingDigitalTwin || !digitalTwin) {
      return []
    }
    const propertyObjects: DigitalTwinPropertyObject[] = []
    digitalTwin.model.name = digitalTwin.name
    digitalTwin.model.level = 'model' as DeviationSettingLevel
    if (isWritableObject(digitalTwin.model)) {
      propertyObjects.push(digitalTwin.model)
    }
    digitalTwin.model.nodes.forEach((n) => {
      setPropertyObject(propertyObjects, 'unit', n.units)
      n.level = 'node' as DeviationSettingLevel
      if (isWritableObject(n)) {
        propertyObjects.push(n)
      }
    })
    digitalTwin.model.commodities.forEach((c) => {
      setPropertyObject(propertyObjects, 'byproduct', c.byproducts)
      c.level = 'commodity' as DeviationSettingLevel

      if (isWritableObject(c)) {
        propertyObjects.push(c)
      }
    })
    setPropertyObject(propertyObjects, 'exchange', digitalTwin.model.exchanges)
    setPropertyObject(propertyObjects, 'balance', digitalTwin.model.balances)
    return propertyObjects
  }, [digitalTwin, isLoadingDigitalTwin])

  const levelChoiceItems = useMemo((): { value: string; label: string }[] => {
    if (!Array.isArray(propertyItems)) {
      return []
    }

    const levelChoices: Choices[] = []
    for (const property of propertyItems) {
      if (property.level && !levelChoices.some((c) => c.value === property.level) && property.configurable_properties) {
        levelChoices.push({
          name: getPrettyName(property.level, digitalTwin?.translations, true),
          value: property.level,
        })
      }
    }

    return levelChoices
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((type) => ({ value: type.value, label: type.name }))
  }, [digitalTwin?.translations, propertyItems])

  const getNameChoiceItems = useMemo(
    () =>
      (index: number): { value: string; label: string }[] => {
        const currentLevel = linkedObjectFromInstructions?.[index]?.level ?? ''
        return (
          propertyItems
            .filter(
              (unit) =>
                unit.level === currentLevel &&
                !unit.tags?.includes('exclude') &&
                (unit.tags?.includes('include_events') ||
                  (unit.configurable_properties && unit.configurable_properties.length > 0))
            )
            .map((unit) => getAutocompleteValueForModelEntityName(unit.name, digitalTwin?.translations))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((type) => ({ value: type.value, label: type.name })) ?? []
        )
      },
    [propertyItems, digitalTwin?.translations, linkedObjectFromInstructions]
  )

  const getAttributeChoiceItems = useMemo(
    () =>
      (index: number): { value: string; label: string }[] => {
        const currentLevel = linkedObjectFromInstructions?.[index]?.level
        const currentName = linkedObjectFromInstructions?.[index]?.name

        return (
          propertyItems
            .find((p) => p.name === currentName && p.level === currentLevel)
            ?.configurable_properties?.filter(
              (p) =>
                !p.tags?.includes('exclude') &&
                !(
                  Array.isArray(p.available_priorities) &&
                  p.available_priorities.length === 1 &&
                  p.available_priorities[0] === BASE_SETTING_PRIO.priority_level
                )
            )
            ?.map((p) => getAutocompleteValueForProperty(p.attribute, digitalTwin?.translations))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((type) => ({ value: type.value, label: type.name })) ?? []
        )
      },
    [linkedObjectFromInstructions, propertyItems, digitalTwin?.translations]
  )
  const getOptionLabelPrettyName = useMemo(
    () =>
      (optionLabel: string, parent?: string): string => {
        if (parent) {
          return getPrettyName(`${parent}.${optionLabel}`, digitalTwin?.translations)
        } else {
          return getPrettyName(optionLabel, digitalTwin?.translations)
        }
      },
    [digitalTwin?.translations]
  )

  if (eventPost.setting_instructions && linkedObjectFromInstructions) {
    return (
      <>
        <Autocomplete
          disablePortal
          disabled={disabled || noSettingWritePermission}
          fullWidth
          options={levelChoiceItems}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          value={
            noSettingWritePermission ? null : levelChoiceItems.find((item) => item.value === linkedObject.level) || null
          }
          inputValue={
            noSettingWritePermission
              ? getPrettyName(linkedObjectFromInstructions?.[index]?.level ?? '', digitalTwin?.translations, true)
              : undefined
          }
          onChange={(_e, newValue) => {
            // clone the linked_objects array and update the item at `index`
            const allLinkedObjects = [...(linkedObjectFromInstructions ?? [])]
            let thisLinkedObject = allLinkedObjects[index]
            thisLinkedObject = {
              ...thisLinkedObject,
              level: newValue?.value || '',
              name: '',
              attribute: '',
            }

            allLinkedObjects[index] = thisLinkedObject
            const updatedEventPost = {
              ...eventPost,
              setting_instructions: {
                ...eventPost.setting_instructions,
                linked_objects: allLinkedObjects,
              },
            }
            setEventPost(updatedEventPost as EventPost | EventChangeRequest)
          }}
          renderInput={(params) => <TextField {...params} label={t('Category') + ' *'} error={error} margin="dense" />}
        />
        <Autocomplete
          disablePortal
          disabled={linkedObjectFromInstructions?.[index].level === '' || disabled || noSettingWritePermission}
          fullWidth
          options={getNameChoiceItems(index)}
          getOptionLabel={(option) =>
            getOptionLabelPrettyName(option.value, linkedObjectFromInstructions?.[index]?.level)
          }
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          value={
            noSettingWritePermission
              ? null
              : getNameChoiceItems(index).find((item) => item.value === linkedObject.name) || null
          }
          inputValue={
            noSettingWritePermission
              ? getOptionLabelPrettyName(
                  linkedObjectFromInstructions?.[index]?.name,
                  linkedObjectFromInstructions?.[index]?.level
                )
              : undefined
          }
          onChange={(_e, newValue) => {
            if (linkedObjectFromInstructions) {
              const allLinkedObjects = [...linkedObjectFromInstructions]

              allLinkedObjects[index] = {
                ...allLinkedObjects[index],
                name: newValue?.value || '',
                attribute: '',
              }
              const newEventPost = {
                ...eventPost,
                setting_instructions: {
                  ...eventPost.setting_instructions,
                  linked_objects: allLinkedObjects,
                },
              }
              setEventPost(newEventPost as EventPost | EventChangeRequest)
            }
          }}
          renderInput={(params) => <TextField {...params} label={t('Object') + ' *'} error={error} margin="dense" />}
        />
        <Autocomplete
          disablePortal
          disabled={linkedObjectFromInstructions?.[index].name === '' || disabled || noSettingWritePermission}
          fullWidth
          options={getAttributeChoiceItems(index)}
          getOptionLabel={(option) =>
            getOptionLabelPrettyName(option.value, linkedObjectFromInstructions?.[index]?.name)
          }
          isOptionEqualToValue={(option, value) => option.value === value?.value}
          value={
            noSettingWritePermission
              ? null
              : getAttributeChoiceItems(index).find((item) => item.value === linkedObject.attribute) || null
          }
          inputValue={
            noSettingWritePermission
              ? getOptionLabelPrettyName(
                  linkedObjectFromInstructions?.[index]?.attribute ?? '',
                  linkedObjectFromInstructions?.[index]?.name
                )
              : undefined
          }
          onChange={(_e, newValue) => {
            if (linkedObjectFromInstructions) {
              const allLinkedObjects = [...linkedObjectFromInstructions]
              allLinkedObjects[index] = {
                ...allLinkedObjects[index],
                attribute: newValue?.value || '',
              }
              const newEventPost = {
                ...eventPost,
                setting_instructions: {
                  ...eventPost.setting_instructions,
                  linked_objects: allLinkedObjects,
                },
              }
              setEventPost(newEventPost as EventPost | EventChangeRequest)
            }
          }}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {option.label}
              {getHelpTextPrettyName(option.value, digitalTwin?.translations) && (
                <Tooltip
                  title={getHelpTextPrettyName(option.value, digitalTwin?.translations)}
                  children={
                    <i
                      style={{ paddingLeft: '5px', color: colors.info4, fontSize: 'small' }}
                      className={'fas fa-info-circle'}
                    />
                  }
                />
              )}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label={t('Property')} error={error} margin="dense" />}
        />
      </>
    )
  } else {
    return <></>
  }
}
